import Badge from '@risk-smart/themed-cloudscape-components/badge';
import type { CardsProps } from '@risk-smart/themed-cloudscape-components/cards';
import Cards from '@risk-smart/themed-cloudscape-components/cards';
import Container from '@risk-smart/themed-cloudscape-components/container';
import Header from '@risk-smart/themed-cloudscape-components/header';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { Icon } from '@risksmart-app/atomic-ui';
import i18n from '@risksmart-app/i18n/src/i18n';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  DrillCount,
  DrillDownColumnHeader,
} from 'src/blocks/drill-down-column';
import { RevealButton } from 'src/blocks/object-toolbar/parts/RevealButton';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import { useGetRiskById } from '@/hooks/queries';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { createFindRisksInTier } from '@/utils/findRisksInTier';
import { getFriendlyId } from '@/utils/friendlyId';
import { riskDetailsUrl } from '@/utils/urls';

import type { RiskRegisterFields } from '../risks/types';
import SelectedRiskAttribute from './SelectedRiskAttribute';
import styles from './style.module.scss';
import type { CardType, DashboardState, RiskAttribute } from './types';

const findRisksInTier = createFindRisksInTier<CardType>((risk) =>
  risk.unlinked ? undefined : risk.ParentRiskId
);

export interface Props {
  dashboardState: DashboardState;
  setDashboardState: (state: DashboardState) => void;
  tier: 1 | 2 | 3;
  tierRisks: readonly RiskRegisterFields[];
  selectedRiskAttribute: RiskAttribute;
}

const Tier: FC<Props> = ({
  tier = 1,
  dashboardState,
  setDashboardState,
  tierRisks,
  selectedRiskAttribute,
}) => {
  /*
   * The compact action is a real anchor, so route a plain left click through the
   * router — carrying the same `from` state the Cloudscape `onFollow` path carried,
   * which is what the breadcrumb reads — and leave modifier clicks to the browser so
   * open-in-new-tab keeps working.
   */
  const navigate = useNavigate();
  const followInternally = (event: MouseEvent<HTMLElement>) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    navigate(addRiskHref, { state: { from: 'risk-dashboard' } });
  };

  const selectedRiskId = dashboardState.get(tier);
  const parentRiskId = dashboardState?.get(tier - 1);

  const { t } = useTranslation(['common']);
  const { t: tt } = useTranslation(['taxonomy']);
  const { t: tr } = useTranslation(['common'], {
    keyPrefix: 'risks',
  });

  const isEnterpriseRiskEnabled = useIsModuleEnabled('enterprise_risk');

  const tiers = t('tiers', { returnObjects: true });

  const tierLabel = tiers[String(tier) as keyof typeof tiers];

  const risks: CardType[] = [
    ...(tierRisks ?? []),
    {
      Id: 'unlinked',
      Title: tr('dashboard.unlinked'),
      Tier: tier,
      unlinked: true,
      Entity: '-',
    },
  ];

  const risksInTier = findRisksInTier(
    tier,
    risks,
    dashboardState,
    parentRiskId
  );

  // The synthetic "Unlinked" bucket is a navigation affordance, not a risk, so it
  // must not inflate the column's count.
  const resultCount = risksInTier.filter((r) => !r.unlinked).length;

  /*
   * Three distinct states, not two. A tier with nothing selected above it has not
   * "found no items" — it is waiting for the drill-down step to its left, and saying
   * so is what makes the cascade discoverable. Tier 1 has no parent, so it only ever
   * reports a real empty.
   */
  const awaitingParentSelection = tier > 1 && !parentRiskId;
  const empty = awaitingParentSelection
    ? tr('dashboard.select_prompt', {
        parent: tiers[String(tier - 1) as keyof typeof tiers],
        tier: tierLabel,
      })
    : resultCount === 0
      ? tr('dashboard.no_items')
      : '';

  /*
   * How many risks in the tier below link to each risk in this one, so a card can show
   * what drilling into it will yield. One pass over the register; the bottom tier
   * drills nowhere, so it is skipped.
   */
  const childCountByParentId = new Map<string, number>();
  if (tier < 3) {
    for (const risk of tierRisks ?? []) {
      if (risk.Tier !== tier + 1 || !risk.ParentRiskId) {
        continue;
      }
      childCountByParentId.set(
        risk.ParentRiskId,
        (childCountByParentId.get(risk.ParentRiskId) ?? 0) + 1
      );
    }
  }

  const onSelectionChange: CardsProps<CardType>['onSelectionChange'] = ({
    detail,
  }) => {
    const newState: DashboardState = new Map();
    dashboardState.forEach((value, key) => {
      if (key === tier) {
        newState.set(key, detail.selectedItems[0].Id);
      } else if (key > tier) {
        newState.set(key, undefined); // Clear all lower tiers
      } else {
        newState.set(key, value);
      }
    });
    setDashboardState(newState);
  };

  const selectedRisk = risks.find((r) => r.Id === selectedRiskId);
  const selectedItems = selectedRisk ? [selectedRisk] : [];

  // The synthetic "Unlinked" card uses a non-UUID id, so treat it as no parent for fetch/add-href.
  const hasRealParent = !!parentRiskId && parentRiskId !== 'unlinked';

  // Fetch the selected parent risk (one tier above) to get ancestorContributors for permission checks
  const shouldSkipParentRiskFetch = tier <= 1 || !hasRealParent;
  const {
    data: parentRiskData,
    loading: isParentRiskLoading,
    error: parentRiskError,
  } = useGetRiskById({
    queryArgs: { riskId: parentRiskId ?? '' },
    shouldSkip: shouldSkipParentRiskFetch,
  });
  const parentRisk = parentRiskData?.risk?.[0] ?? null;

  const addRiskHref =
    tier > 1 && hasRealParent
      ? `/risks/${parentRiskId}/add?tier=${tier}`
      : `/risks/add?tier=${tier}`;

  return (
    <div className={styles.tier} data-testid={`tier-${tier}`}>
      <Container fitHeight variant={'stacked'}>
        <SpaceBetween direction={'vertical'} size={'m'}>
          <DrillDownColumnHeader
            title={tierLabel}
            counter={
              awaitingParentSelection || resultCount === 0
                ? undefined
                : `(${resultCount})`
            }
            action={
              !shouldSkipParentRiskFetch &&
              (isParentRiskLoading || parentRiskError) ? undefined : (
                <Permission
                  permission={'insert:risk'}
                  canHaveAccessAsContributor={tier > 1}
                  parentObject={parentRisk}
                >
                  {/*
                    Compact icon rather than a pill: a ~96px pill cannot share a
                    drill-down column's header row without squeezing the title (see
                    COI-739). The label is tier-qualified so the icon is never unnamed.
                  */}
                  <RevealButton
                    label={tr('dashboard.add_labelled', { tier: tierLabel })}
                    reveal={'none'}
                    href={addRiskHref}
                    onClick={followInternally}
                  >
                    <Icon name={'plus'} size={'lg'} />
                  </RevealButton>
                </Permission>
              )
            }
          />
          <Cards<CardType>
            ariaLabels={{
              // `{{title}}`, not `$0` — the latter is not i18next interpolation, so
              // this label used to announce the literal string "Select $0".
              itemSelectionLabel: (e, n) =>
                tr('dashboard.select', { title: n.Title }),
              selectionGroupLabel: tr('dashboard.item_selection'),
            }}
            entireCardClickable={true}
            cardDefinition={{
              sections: [
                {
                  id: 'title',
                  content: (item) => {
                    const childCount = item.unlinked
                      ? 0
                      : (childCountByParentId.get(item.Id) ?? 0);

                    return (
                      <div
                        className={
                          'flex w-full items-center justify-between gap-2'
                        }
                      >
                        <div className={'inline-block'}>
                          <Link
                            variant={'secondary'}
                            href={
                              !item.unlinked
                                ? riskDetailsUrl(item.Id)
                                : undefined
                            }
                          >
                            <Header
                              variant={'h3'}
                              data-unlinked={item.unlinked}
                            >
                              <span className={'text-base'}>{item.Title}</span>
                            </Header>
                          </Link>
                          {isEnterpriseRiskEnabled && (
                            <span className={'text-grey text-sm'}>
                              {item.Entity}
                            </span>
                          )}
                        </div>
                        {/* Absent on a card with no children, so a dead end reads
                            as one. */}
                        {childCount > 0 && (
                          <DrillCount
                            count={childCount}
                            label={tr('dashboard.child_count', {
                              count: childCount,
                            })}
                          />
                        )}
                      </div>
                    );
                  },
                },
              ],
              header: (item) => (
                <div className={'flex'}>
                  {!item.unlinked && (
                    <div className={'text-grey text-sm flex-grow'}>
                      {getFriendlyId(Parent_Type_Enum.Risk, item.SequentialId)}
                      {isEnterpriseRiskEnabled ? (
                        item.enterpriseRiskInstance?.enterpriseRisk ? (
                          <Badge className={styles.badgeEnterprise}>
                            {i18n.format(tt('enterprise_one'), 'capitalize')}
                          </Badge>
                        ) : (
                          <Badge className={styles.badgeEntity}>
                            {i18n.format(tt('legal_entity_one'), 'capitalize')}
                          </Badge>
                        )
                      ) : null}
                    </div>
                  )}

                  <SelectedRiskAttribute
                    data={item}
                    selectedRiskAttribute={selectedRiskAttribute}
                  />
                </div>
              ),
            }}
            cardsPerRow={[{ cards: 1 }]}
            items={risksInTier}
            empty={empty}
            loadingText={tr('loading_message')}
            visibleSections={['title']}
            selectionType={'single'}
            selectedItems={selectedItems}
            onSelectionChange={onSelectionChange}
            trackBy={(item) => item.Id}
          />
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default Tier;
